import React, { useState, useEffect, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationTriangle, faAngleDown, faCheckSquare, faSpinner, faCheck, faUndo, faRedo } from '@fortawesome/free-solid-svg-icons';
import { faSquare } from '@fortawesome/free-regular-svg-icons';

import 'bulma/css/bulma.min.css';

import { Parameter, ParameterSlug } from '../scanTypes.ts';
import { BeamlineContext, BeamlineLoadingState } from '../beamlineProvider.tsx';
import { LoadingBanner, LoadingState } from '../components/loadingBanner.tsx';
import { EditAsChoice } from '../api/api.ts';
import { Coordinates, EditableTable, EditableTableCellSettings } from '../components/editableTable.tsx';
import { CellValidationStatus, CellValidationResult } from '../components/editableTableCell/cellDto.ts';
import ChoicesCellModalContent from './choicesCellModalContent.tsx';
import ValidationCellModalContent from './validationCellModalContent.tsx';
import AddParameter from './addParameter.tsx';
import DeleteParameters from './deleteParameters.tsx';
import { Uid, truthyJoin, parseAndGetPrecision, timestampToTodayString, sortWithNumberParsing } from '../utils.tsx';


// It's a magic number I know, and that's annoying.
// This is the number of columns to display in the table.
const FIXED_COLUMN_COUNT = 13;

enum SyncState { Idle, Pending, Requested, Failed, Completed };


// This is a standard data structure used to indicate the selection of
// a single field, or parameter, in a SampleConfiguration object.
// For example the sample editing table has columns that refer to fields like "Name", "Description", etc,
// but also has columns for parameters.  This structure can be used to indicate one of those columns.
enum ParameterField {
  Slug, Name, Description, EditAs, Default, UniqueInSet, AutoGenerateInterval, Choices,
  Validation, SchemaIdentifier, DerivedFrom, DisplayOrder, LastModified
};
const parameterFieldDisplayOrder: ParameterField[] = [
  ParameterField.Slug, ParameterField.Name, ParameterField.Description, ParameterField.EditAs, ParameterField.Default,
  ParameterField.UniqueInSet, ParameterField.AutoGenerateInterval, ParameterField.Choices,
  ParameterField.Validation, ParameterField.SchemaIdentifier, ParameterField.DerivedFrom,
  ParameterField.DisplayOrder, ParameterField.LastModified
];
const columnNames = [
  "Id", "Name", "Description", "Edit As", "Default", "Unique In Set", "Auto Generate Interval", "Choices",
  "Validation", "Schema Id", "Derived From", "Display Order", "Last Modified"
];


const ParameterTable: React.FC = () => {

  const beamlineContext = useContext(BeamlineContext);
  const [loading, setLoading] = useState<LoadingState>(LoadingState.Loading);
  const [loadingMessage, setLoadingMessage] = useState("");

  const [sortedParameterIds, setSortedParameterIds] = useState<Uid[]>([]);
  const [selectedParameterIds, setSelectedParameterIds] = useState<Uid[]>([]);
  // We have a few columns hidden by default
  const [hiddenColumnSet, setHiddenColumnSet] = useState<Set<number>>(
    new Set([ParameterField.Slug, ParameterField.EditAs, ParameterField.SchemaIdentifier, ParameterField.DerivedFrom, ParameterField.LastModified]));

  const [tableSortColumn, setTableSortColumn] = useState<ParameterField>(ParameterField.Name);
  const [tableSortReverse, setTableSortReverse] = useState<boolean>(false);
  
  const [syncTimer, setSyncTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [syncState, setSyncState] = useState<SyncState>(SyncState.Idle);

  useEffect(() => {
    if (beamlineContext.loadingState == BeamlineLoadingState.Failed) {
      setLoading(LoadingState.Failure);
      setLoadingMessage("Failed to load Beamline. Are you sure you're still logged in?");
      return;
    } 
    if (beamlineContext.loadingState == BeamlineLoadingState.Succeeded) {
      setSortedParameterIds(beamlineContext.scanTypes.parameterIdsInDisplayOrder);
      setLoading(LoadingState.Success);
      return;
    }
    setLoading(LoadingState.Loading);
    setLoadingMessage("Loading Beamline...");
  }, [beamlineContext.changeCounter, beamlineContext.loadingState]);


  function sortParameterIds(field: ParameterField, reverse: boolean): Uid[] {
    const parameters = Array.from(beamlineContext.scanTypes.parametersById.values());
    var sorted:Parameter[] = [];
    switch (field) {
      case ParameterField.Slug:
        sorted = parameters.sort((a, b) => { return sortWithNumberParsing(a.slug, b.slug, reverse)});
        break;
      case ParameterField.Name:
        sorted = parameters.sort((a, b) => { return sortWithNumberParsing(a.name, b.name, reverse)});
        break;
      case ParameterField.Description:
        sorted = parameters.sort((a, b) => { return sortWithNumberParsing(a.description, b.description, reverse)});
        break;
      case ParameterField.EditAs:
        sorted = parameters.sort((a, b) => { return sortWithNumberParsing(a.editAs, b.editAs, reverse)});
        break;
      case ParameterField.Default:
        sorted = parameters.sort((a, b) => { return sortWithNumberParsing(a.standardDefault, b.standardDefault, reverse)});
        break;
      case ParameterField.UniqueInSet:
        sorted = parameters.sort(
            (a, b) => { return sortWithNumberParsing(a.name, b.name, reverse)}
          ).sort(
            (a, b) => { return a.enforceUniqueInSet === b.enforceUniqueInSet ? 0 : (reverse ? b.enforceUniqueInSet : a.enforceUniqueInSet) ? -1 : 1 }
          );
        break;
      case ParameterField.AutoGenerateInterval:
        sorted = parameters.sort((a, b) => { return sortWithNumberParsing(a.autoGenerateInterval, b.autoGenerateInterval, reverse)});
        break;
      case ParameterField.Choices:
        sorted = parameters.sort((a, b) => {
          const aChoices = a.choices ? a.choices.map((c) => c.name).join(", ") : "";
          const bChoices = b.choices ? b.choices.map((c) => c.name).join(", ") : "";
          return sortWithNumberParsing(aChoices, bChoices, reverse)
        });
        break;
      case ParameterField.Validation:
        sorted = parameters.sort((a, b) => {
          // Serialize the validation JSON object to a string for sorting purposes.
          const aValidation = a.validatorObject ? JSON.stringify(a.validatorObject) : "";
          const bValidation = b.validatorObject ? JSON.stringify(b.validatorObject) : "";
          return sortWithNumberParsing(aValidation, bValidation, reverse)
        });
        break;
      case ParameterField.SchemaIdentifier:
        sorted = parameters.sort((a, b) => { return sortWithNumberParsing(a.schemaIdentifier, b.schemaIdentifier, reverse)});
        break;
      case ParameterField.DerivedFrom:
        sorted = parameters.sort((a, b) => {
          const aName = a.idDerivedFrom ? (beamlineContext.scanTypes.parametersById.get(a.idDerivedFrom)?.name || "") : "";
          const bName = b.idDerivedFrom ? (beamlineContext.scanTypes.parametersById.get(b.idDerivedFrom)?.name || "") : "";
          return sortWithNumberParsing(aName, bName, reverse)
        });
        break;
      case ParameterField.DisplayOrder:
        sorted = parameters.sort((a, b) => { return reverse ? b.order - a.order : a.order - b.order });
        break;
      case ParameterField.LastModified:
        sorted = parameters.sort((a, b) => {
          const aDate = Date.parse(a.lastModified);
          const bDate = Date.parse(b.lastModified);
          return reverse ? bDate - aDate : aDate - bDate;
        });
        break;
    }
    return sorted.map((s) => s.id);
  }


  function clickedTableSortHeader(f:ParameterField) {
    const sortDirection = (f == tableSortColumn) ? !tableSortReverse : tableSortReverse;
    setTableSortColumn(f);
    setTableSortReverse(sortDirection);
    setSortedParameterIds(sortParameterIds(f, sortDirection));
  }


  function toggleHiddenColumn(index: number) {
    const newHiddenColumnSet = new Set(hiddenColumnSet);
    if (hiddenColumnSet.has(index)) {
      newHiddenColumnSet.delete(index);
    } else {
      newHiddenColumnSet.add(index);
    }
    setHiddenColumnSet(newHiddenColumnSet);
  }


  // Stubbed for now
  function tableOnCopy(event: React.ClipboardEvent, rectLower: Coordinates, rectHigher: Coordinates) {
    return;
  }


  // Stubbed for now
  function tableOnPaste(event: React.ClipboardEvent, rectLower: Coordinates, rectHigher: Coordinates) {
    return;
  }


  // Add the vector to the given currentPosition until it points to a table cell that is
  // valid for editing, then return that position, or return null if we run off the edge of the table.
  function isCellValidForEditing(coordinates: Coordinates): boolean {
    const fieldType = parameterFieldDisplayOrder[coordinates.x];
    if (fieldType == ParameterField.LastModified || fieldType == ParameterField.DerivedFrom) {
      return false;
    }
    return true;
  }


  // A function passed to every EditableTableCell (non-header table cell in samples table)
  // and used when rendering the table,
  // that validates the given input value based on its cell's x,y location in the table.
  // It relies on the displayedParameters constant, calculated just above.
  function cellValidator(x: number, y: number, value: any): CellValidationResult {
    switch (parameterFieldDisplayOrder[x]) {

      case ParameterField.Slug:
        if (value == "") {
          return { status: CellValidationStatus.Failure, message: "Id cannot be blank." };
        } else if (value.search(/[^A-Za-z0-9\-_]/g) >= 0) {
          return { status: CellValidationStatus.Failure, message: "Id must consist only of letters, numbers, underscore, or dash." };
        } else {
          if (sortedParameterIds.some((id) => {
            const p = beamlineContext.scanTypes.parametersById.get(id);
            if (!p) { return false; }
            if (p.id == sortedParameterIds[y]) { return false; }
            return p.slug == value;
          })) {
            return { status: CellValidationStatus.Failure, message: "Id must be unique for beamline." };
          }
        }
        break;
      case ParameterField.Name:
        if (value == "") {
          return { status: CellValidationStatus.Failure, message: "Name cannot be blank." };
        } else {
          if (sortedParameterIds.some((id) => {
            const p = beamlineContext.scanTypes.parametersById.get(id);
            if (!p) { return false; }
            if (p.id == sortedParameterIds[y]) { return false; }
            return p.name === value;
          })) {
            return { status: CellValidationStatus.Failure, message: "Name must be unique for beamline." };
          }
        }
        break;
      case ParameterField.DisplayOrder:
        const valueAsInt = parseInt(value,10);
        if (isNaN(valueAsInt) || valueAsInt < 0 || valueAsInt >= sortedParameterIds.length) {
          return { status: CellValidationStatus.Failure, message: "Display order must be a number between 0 and " + (sortedParameterIds.length - 1) + "." };
        }
        break;
      case ParameterField.Choices:
        // Value must be blank, or successfully parse as a JSON array of objects with "name" properties.
        if (value === "" || value === undefined) {
          break;
        }
        let possibleObject = value;
        if (typeof possibleObject === "string") {
          try {
            possibleObject = JSON.parse(value);
          } catch (e) {
            return { status: CellValidationStatus.Failure, message: "Must be a JSON array." };
          }
        }
        if (!Array.isArray(possibleObject)) {
          return { status: CellValidationStatus.Failure, message: "Must be a JSON array." };
        }
        for (const item of possibleObject) {
          if (typeof item !== "object" || !("name" in item) || !("description" in item)) {
            return { status: CellValidationStatus.Failure, message: "Each choice must be an object with a 'name' and a 'description' property." };
          }
          if ((item["name"] || "").trim() == "") {
            return { status: CellValidationStatus.Failure, message: "Choice names cannot be blank." };
          }
        }
        break;
    }
    return { status: CellValidationStatus.Success, message: null };
  }


  // A function passed to every EditableTableCell (non-header table cell in samples table)
  // that saves the given input value to the SampleConfiguration indicated by the
  // table cell at the given x,y location.
  // It relies on the displayedParameters constant, calculated just above.
  function cellSave(x: number, y: number, newValue: any): CellValidationResult {

    // When editing Parameters, we refuse to save any value that doesn't pass validation.
    const validationResult = cellValidator(x, y, newValue);
    if (validationResult.status != CellValidationStatus.Success) {
      return validationResult;
    }

    const thisParam = beamlineContext.scanTypes.parametersById.get(sortedParameterIds[y]);
    if (!thisParam) {
      return { status: CellValidationStatus.Failure, message: "Error: Parameter does not exist!" }
    }

    const editedParam = thisParam.clone();

    let editedParams = [editedParam];
    switch (parameterFieldDisplayOrder[x]) {
      case ParameterField.Slug:
        editedParam.slug = (newValue as string).toLowerCase().replace(/[^a-z0-9\-_]/g, "_") as ParameterSlug;
        break;
      case ParameterField.Name:
        editedParam.name = newValue as string;
        break;
      case ParameterField.Description:
        editedParam.description = newValue;
        break;
      case ParameterField.EditAs:
        editedParam.editAs = newValue as EditAsChoice;
        break;
      case ParameterField.Default:
        editedParam.standardDefault = (newValue as string).trim() == "" ? undefined : newValue;
        break;
      case ParameterField.UniqueInSet:
        editedParam.enforceUniqueInSet = newValue as boolean;
        break;
      case ParameterField.AutoGenerateInterval:
        const parsedFloat = parseAndGetPrecision(newValue, "");
        // NaN and 0 both evaluate to false, so we turn them into "undefined", since neither is an acceptable interval.
        editedParam.autoGenerateInterval = parsedFloat[0] || undefined;
        break;
      case ParameterField.Choices:
        editedParam.choices = newValue;
        break;
      case ParameterField.Validation:
        editedParam.validatorObject = newValue;
        break;
        case ParameterField.SchemaIdentifier:
        editedParam.schemaIdentifier = (newValue as string).trim() == "" ? undefined : newValue;
        break;
      case ParameterField.DisplayOrder:
        const parsedOrder = parseInt(newValue,10);
        if (!isNaN(parsedOrder) && parsedOrder >= 0 && parsedOrder < sortedParameterIds.length) {
          const currentOrderWithoutEdited = beamlineContext.scanTypes.parameterIdsInDisplayOrder.filter((id) => id != editedParam.id);
          const newOrder = [...currentOrderWithoutEdited.slice(0, parsedOrder), editedParam.id, ...currentOrderWithoutEdited.slice(parsedOrder)];
          editedParams = newOrder.map((id, index) => {
            const p = beamlineContext.scanTypes.parametersById.get(id)!;
            const clone = p.clone();
            clone.order = index;
            return clone;
          });
        }
        break;
      }

    beamlineContext.scanTypes.replaceWithHistory(editedParams);
    beamlineContext.changed();
    contentChanged();

    return { status: CellValidationStatus.Success, message: null };
  }


  function clickedUndo() {
    beamlineContext.scanTypes.undo();
    beamlineContext.changed();
    contentChanged();
  }


  function clickedRedo() {
    beamlineContext.scanTypes.redo();
    beamlineContext.changed();
    contentChanged();
  }


  function contentChanged() {
    if (syncTimer) { clearTimeout(syncTimer); }
    setSyncTimer(setTimeout(() => syncTimerExpired(), 1500));
    if (syncState != SyncState.Requested) {
      setSyncState(SyncState.Pending);
    }
  }


  async function syncTimerExpired() {
    // If another save is pending while the previous one is still
    // in progress (requested), we renew the timer and exit, until the previous
    // one reports either Completed or Failed.
    if (syncState == SyncState.Requested) {
      setSyncTimer(setTimeout(() => syncTimerExpired(), 1000));
      return;
    }

    setSyncTimer(null);
    const changes = beamlineContext.scanTypes.getPendingChanges();
    if (!changes) {
      setSyncState(SyncState.Idle);
      return;
    }

    setSyncState(SyncState.Requested);
    const response = await beamlineContext.scanTypes.catchUpEdits()
    if (response.success) {
      setSyncState(SyncState.Completed);
    } else {
      setSyncState(SyncState.Failed);
    }
  }


  function tableRowSelectionChanged(rowIndexes: number[]) {
    var newSelectedParameterIds = rowIndexes.map((i) => {
      return sortedParameterIds[i];
    });
    setSelectedParameterIds(newSelectedParameterIds);
  }


  function renderHeaderCell(x: number): React.JSX.Element {
    const sortDirectionStyle = tableSortReverse ? "sortedup" : "sorteddown";
    const fieldType = parameterFieldDisplayOrder[x];
    const fieldName = columnNames[x];

    return (<th key={fieldName}
            className={truthyJoin("sortable", (tableSortColumn == fieldType) && sortDirectionStyle)}
            onClick={ () => clickedTableSortHeader(fieldType) }
            scope="col">{fieldName}</th>);
  }


  // Table cells related to the 'choices' field in a Parameter are edited using a custom modal,
  // and this function links the custom modal content to the modal render function.
  function renderChoiceCellCustomModalContent(value: any, onValueChange: (input: any) => void): React.JSX.Element {
    return (<ChoicesCellModalContent value={value} onValueChange={onValueChange} />);
  }

  function renderValidationCellCustomModalContent(value: any, onValueChange: (input: any) => void): React.JSX.Element {
    return (<ValidationCellModalContent value={value} onValueChange={onValueChange} />);
  }

  // Function provided to the EditableTable.  Given coordinates, renders an editable cell.
  // The editable cell is given the activationStatus and cellFunctions prepared by the EditableTable.
  function getTableCellSettings(coordinates: Coordinates): EditableTableCellSettings {
    const thisParam = beamlineContext.scanTypes.parametersById.get(sortedParameterIds[coordinates.y]);
    if (!thisParam) {
      return {
        key: `${coordinates.x}-${coordinates.y}-invalid`,
        isInvalid: false,
        isReadOnly: true,
        isUnused: true,
        value: null,
      };
    }

    // Make a lookup table of values, then pass the appropriate one to the validator.
    // If validation doesn't pass, set a flag that will be applied to the call.
    const valuesArray = [
      thisParam.slug, thisParam.name, thisParam.description, thisParam.editAs, thisParam.standardDefault,
      thisParam.enforceUniqueInSet, thisParam.autoGenerateInterval, thisParam.choices,
      thisParam.validatorObject, thisParam.schemaIdentifier, thisParam.idDerivedFrom,
      thisParam.order, thisParam.lastModified
    ];
    const cellValue = valuesArray[coordinates.x];
    const validationResult = cellValidator(coordinates.x, coordinates.y, cellValue);
    const isInvalid = validationResult.status == CellValidationStatus.Success ? false : true;

    // Standard settings for every cell in the row.
    const cellSettings:EditableTableCellSettings = {
      key: `${coordinates.x}-${coordinates.y}`,
      isInvalid: isInvalid,
      isUnused: false,
      isReadOnly: false,
      value: cellValue,
    }

    // Set the value for the cell, making any conversions needed, and apply additional per-column settings.
    switch (parameterFieldDisplayOrder[coordinates.x]) {
      case ParameterField.Slug:
        cellSettings.description="Id for the Parameter, used in the API. Must be unique for beamline, and can only contain letters, numbers, dash, and underscore.";
        break;
      case ParameterField.Name:
        cellSettings.description="Name for the Parameter, shown in the UI. Must be unique for beamline.";
        break;
      case ParameterField.Description:
        cellSettings.value = cellValue ?? "";
        cellSettings.description="Description for the Parameter. Displayed in the Sample table when the user is editing the value.";
        break;
      case ParameterField.EditAs:
        cellSettings.value = cellValue ?? EditAsChoice.Inline;
        cellSettings.description="How this Parameter should be edited in the UI.";
        cellSettings.choices = [
          { name: EditAsChoice.Inline, description: "The default. The value is edited directly in the cell." },
          { name: EditAsChoice.Pulldown, description: "Edited by selecting from a pulldown. (Like this one!) Requires choices to be defined." },
          { name: EditAsChoice.Textbox, description: "Edited as a free-form multiline text box." },
          { name: EditAsChoice.JSON, description: "Edited as a JSON object in a text box." }
        ];
        cellSettings.description = "Note: This setting only affects the way the user edits the value in the Sample table. All values are stored as JSON internally.";
        break;
      case ParameterField.Default:
        cellSettings.value = thisParam.standardDefault ?? "";
        cellSettings.description="Default value for the Parameter.";
        break;
      case ParameterField.UniqueInSet:
        cellSettings.isBoolean = true;
        cellSettings.description="Whether the Parameter value must be unique within a set.";
        break;
      case ParameterField.AutoGenerateInterval:
        cellSettings.value = (thisParam.autoGenerateInterval ?? "").toString();
        cellSettings.description="Interval for auto-generating values for the Parameter.";
        break;
      case ParameterField.Choices:
        cellSettings.useModal = true;
        cellSettings.modalTitle = "Edit Pulldown Choices";
        cellSettings.customModalContent = renderChoiceCellCustomModalContent;
        cellSettings.value = thisParam.choices;
        cellSettings.displayValue = "";
        if (Array.isArray(thisParam.choices) && thisParam.choices.length > 0) {
          if (thisParam.choices.length == 1) {
            cellSettings.displayValue = `${thisParam.choices[0].name}`;
          } else {
            cellSettings.displayValue = `${thisParam.choices[0].name} (+${thisParam.choices.length - 1} more)`;
          }
         }
        cellSettings.description="List of possible choices. Used when the Parameter is being edited as a pulldown. (See the \"Edit As\" column.)";
        break;
      case ParameterField.Validation:
        cellSettings.useModal = true;
        cellSettings.modalTitle = "Edit Validation Rules";
        cellSettings.customModalContent = renderValidationCellCustomModalContent;
        cellSettings.value = thisParam.validatorObject;
        cellSettings.displayValue = "";
        if (thisParam.validatorObject) {
          cellSettings.displayValue = "Set";
        } else {
          cellSettings.displayValue = "";
        }
        cellSettings.description="Validation rules for the Parameter. See documentation for details.";
        break;
      case ParameterField.SchemaIdentifier:
        cellSettings.value = thisParam.schemaIdentifier ?? "";
        cellSettings.description="Optional schema identifier for the Parameter. See documentation for details.";
        break;
      case ParameterField.DerivedFrom:
        cellSettings.isReadOnly = true;
        cellSettings.value = thisParam.idDerivedFrom ? (beamlineContext.scanTypes.parametersById.get(thisParam.idDerivedFrom)?.name || "") : "";
        cellSettings.description="The Parameter this one was initially cloned from, if any.";
        break;
      case ParameterField.DisplayOrder:
        cellSettings.value = thisParam.order;
        cellSettings.description="Display order of the Parameter in the UI.  In the table for editing Samples, Parameters are displayed as columns, going from left to right in this order.";
        break;
      case ParameterField.LastModified:
        cellSettings.isReadOnly = true;
        cellSettings.value = timestampToTodayString(Date.parse(thisParam.lastModified) / 1000);
        cellSettings.description="The last time this Parameter was modified.";
        break;
    }

    return cellSettings;
  }


  const editableTableFunctions = {
    renderHeaderCell: renderHeaderCell,
    getTableCellSettings: getTableCellSettings,
    isCellValidForEditing: isCellValidForEditing,
    cellValidate: cellValidator,
    cellSave: cellSave,
    onCopy: tableOnCopy,
    onPaste: tableOnPaste,
    rowSelectionChanged: tableRowSelectionChanged
  };


  // If we're in any loading state other than success,
  // display a loading banner instead of the content.
  if (loading != LoadingState.Success) {
    return (<LoadingBanner state={loading} message={loadingMessage}></LoadingBanner>)
  }

  // Construct a message reporting the sync status between this page and the server

  var syncStatusMessage: React.JSX.Element | null = null;
  if (syncState == SyncState.Requested) {
    syncStatusMessage = (<p className="subtitle is-6"><FontAwesomeIcon icon={faSpinner} spin={true} /> Saving Changes</p>); 
  } else if (syncState == SyncState.Completed) {
    syncStatusMessage = (<p className="subtitle is-6"><FontAwesomeIcon icon={faCheck} /> Changes Saved</p>);
  } else if (syncState == SyncState.Failed) {
    syncStatusMessage = (<p className="subtitle is-6"><FontAwesomeIcon icon={faExclamationTriangle} color="darkred" /> Error Saving! Are you logged in?</p>);
  }

  return (
    <>
      <nav className="level">
        <div className="level-left">
          <div className="level-item">
            <p className="subtitle is-5"><strong>{ sortedParameterIds.length }</strong> parameters defined for { beamlineContext.beamline!.name }.</p>
          </div>
          <div className="level-item">
            <AddParameter />
          </div>
        </div>

        <div className="level-right">
          <div className="level-item">
              { syncStatusMessage }
          </div>
          <div className="level-item">
            <div className="dropdown is-right is-hoverable">
              <div className="dropdown-trigger">
                <button className="button is-small is-ghost" aria-haspopup="true" aria-controls="dropdown-menu-columns">
                  <p className="subtitle is-6">Columns</p>
                  <span className="icon is-small">
                    <FontAwesomeIcon icon={faAngleDown} />
                  </span>
                </button>
              </div>
              <div className="dropdown-menu" id="dropdown-menu-columns" role="menu">
                <div className="dropdown-content">
                  { parameterFieldDisplayOrder.map((_, index) => {
                    return (
                      <a href="#" className="dropdown-item" onClick={ () => { toggleHiddenColumn(index) } }>
                        <FontAwesomeIcon icon={hiddenColumnSet.has(index) ? faSquare : faCheckSquare} />
                        <span style={{ marginLeft: "0.5em" }}>{ columnNames[index] }</span>
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          <div className="level-item">
            <div className="field has-addons">
              <p className="control">
                <button className="button" onClick={ clickedUndo } title="Undo" disabled={!beamlineContext.scanTypes.canUndo()}>
                  <FontAwesomeIcon icon={faUndo} />
                </button>
              </p>
              <p className="control">
                <button className="button" onClick={ clickedRedo } title="Redo" disabled={!beamlineContext.scanTypes.canRedo()}>
                  <FontAwesomeIcon icon={faRedo} />
                </button>
              </p>
            </div>
          </div>
          { selectedParameterIds.length > 0 && (
            <div className="level-item">
              <div className="dropdown is-right is-hoverable">
                <div className="dropdown-trigger">
                  <button className="button is-small" aria-haspopup="true" aria-controls="dropdown-menu-parameters">
                    <p className="subtitle is-6">{ selectedParameterIds.length } selected parameter{selectedParameterIds.length > 1 ? 's' : ''}</p>
                    <span className="icon is-small">
                      <FontAwesomeIcon icon={faAngleDown} />
                    </span>
                  </button>
                </div>
                <div className="dropdown-menu" id="dropdown-menu-parameters" role="menu">
                  <div className="dropdown-content">
                    <DeleteParameters selection={selectedParameterIds} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>

      <div className="block" style={{ overflowX: "auto" }}>
        { sortedParameterIds.length > 0 && (
          <>
            <EditableTable
                rowIdentifiers={ sortedParameterIds as unknown as string[] }
                columnCount={ FIXED_COLUMN_COUNT }
                displayRowSelectionBoxes={ true }
                hiddenColumns={ Array.from(hiddenColumnSet) }
                tableFunctions={ editableTableFunctions }
              />
          </>
        )}
      </div>
    </>
  )
}

export default ParameterTable
